// ═══════════════════════════════════════════════════════════════════
// CLIENT-SIDE ENCRYPTION
//
// Threat model: the server (Cloudflare Pages Functions + D1) is treated
// as untrusted storage. It must never be able to read the financial
// data, and the passphrase must never leave this device.
//
// Key schedule — one expensive derivation, two cheap sub-keys:
//
//   master   = PBKDF2-SHA256(passphrase, APP_SALT, 600_000 iterations)
//   encKey   = HKDF-SHA256(master, info="aes-gcm-key")   -> AES-256-GCM
//   vaultId  = HKDF-SHA256(master, info="vault-id")      -> 32-byte hex
//
// The server only ever learns `vaultId`. Because vaultId and encKey are
// separate HKDF branches, holding vaultId gives no purchase on encKey —
// recovering the key would mean brute-forcing the passphrase through
// 600k PBKDF2 iterations.
//
// The passphrase IS the access control. There are no accounts and no
// user table. Losing the passphrase means the data is unrecoverable,
// which is exactly why the export/import backup exists.
// ═══════════════════════════════════════════════════════════════════

const PBKDF2_ITERATIONS = 600_000; // OWASP guidance for PBKDF2-SHA256
const APP_SALT = new TextEncoder().encode('jj-retirement-planner/v1/pbkdf2-salt');

const enc = new TextEncoder();
const dec = new TextDecoder();

// ── base64 helpers (byte-safe) ──
export function toB64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
export function fromB64(b64) {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
function toHex(bytes) {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── Key derivation ──
async function deriveMaster(passphrase) {
  // NFKC-normalise so the same typed passphrase derives the same key on
  // iOS, iPadOS and Windows regardless of Unicode composition.
  const baseKey = await crypto.subtle.importKey(
    'raw', enc.encode(passphrase.normalize('NFKC')), 'PBKDF2', false, ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: APP_SALT, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey, 256
  );
  return new Uint8Array(bits);
}

async function hkdf(master, info, bitLength) {
  const key = await crypto.subtle.importKey('raw', master, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: enc.encode(info) },
    key, bitLength
  );
  return new Uint8Array(bits);
}

/**
 * Turn a passphrase into an unlocked vault identity.
 * Expensive by design (~1-2s on an iPhone) — call once, then reuse.
 * @returns {{ vaultId: string, key: CryptoKey }}
 */
export async function unlock(passphrase) {
  if (!passphrase || passphrase.length < 8) {
    throw new Error('Passphrase must be at least 8 characters.');
  }
  const master = await deriveMaster(passphrase);
  const [rawKey, rawId] = await Promise.all([
    hkdf(master, 'aes-gcm-key', 256),
    hkdf(master, 'vault-id', 256),
  ]);
  master.fill(0); // best-effort scrub
  // Extractable so an explicit, opt-in "stay unlocked" can cache the derived
  // KEY on the device instead of the passphrase itself. The passphrase is
  // likely reused elsewhere in the user's life; the key is not, and it is
  // useless against any other system.
  const key = await crypto.subtle.importKey('raw', rawKey, 'AES-GCM', true, ['encrypt', 'decrypt']);
  rawKey.fill(0);
  return { vaultId: toHex(rawId), key };
}

// ── Optional "stay unlocked on this device" ─────────────────────────
// Off by default. Anyone who can get into the device can then open the
// app, which is why the lock screen states that plainly.

const REMEMBER_KEY = 'jj_vault_unlocked_v1';

export async function rememberKey(vaultId, key) {
  const raw = await crypto.subtle.exportKey('raw', key);
  localStorage.setItem(REMEMBER_KEY, JSON.stringify({ vaultId, k: toB64(new Uint8Array(raw)) }));
}

export function forgetKey() {
  try { localStorage.removeItem(REMEMBER_KEY); } catch { /* ignore */ }
}

export function hasRememberedKey() {
  try { return !!localStorage.getItem(REMEMBER_KEY); } catch { return false; }
}

/** @returns {{vaultId, key}|null} */
export async function loadRememberedKey() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const { vaultId, k } = JSON.parse(raw);
    if (!vaultId || !k) return null;
    const key = await crypto.subtle.importKey('raw', fromB64(k), 'AES-GCM', true, ['encrypt', 'decrypt']);
    return { vaultId, key };
  } catch { return null; }
}

// ── Encrypt / decrypt ──

/**
 * Encrypt any JSON-serialisable value.
 * A fresh 96-bit IV is generated per call — never reused.
 * @returns {{ iv: string, ct: string }} both base64
 */
export async function encryptJSON(key, value) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = enc.encode(JSON.stringify(value));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { iv: toB64(iv), ct: toB64(new Uint8Array(ct)) };
}

/**
 * Decrypt a payload produced by encryptJSON.
 * Throws WrongPassphraseError if the GCM authentication tag does not
 * verify — which is precisely what a wrong passphrase looks like.
 */
export async function decryptJSON(key, { iv, ct }) {
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64(iv) }, key, fromB64(ct)
    );
    return JSON.parse(dec.decode(plain));
  } catch {
    throw new WrongPassphraseError();
  }
}

export class WrongPassphraseError extends Error {
  constructor() {
    super('Could not decrypt this data. The passphrase is wrong, or the data is corrupt.');
    this.name = 'WrongPassphraseError';
  }
}

/**
 * A short, non-secret fingerprint of the vault, shown on the lock screen
 * so James and Joanne can confirm at a glance that both devices derived
 * the SAME vault — catching a typo before it silently creates a second,
 * empty vault. Derived from the public vaultId only, never the key.
 */
export function vaultFingerprint(vaultId) {
  return vaultId.slice(0, 4).toUpperCase() + '-' + vaultId.slice(4, 8).toUpperCase();
}
